import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTimes,
    faSpinner,
    faCalendarPlus,
    faFileText,
    faUsers,
    faUserPlus,
    faPaste,
    faKeyboard,
    faPlus,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { EventMeta, AnonymousUser, ShareRatios } from '../../types';
import { DEFAULT_SHARE_RATIO, MAX_SHARE_RATIO, generateId, getShareRatio } from '../../utils/helpers';
import { ref, set } from 'firebase/database';
import { database } from '../../firebase/firebase';
import { useTranslation } from 'react-i18next';
import HelpTip from '../common/HelpTip';

interface EditEventModalProps {
    event: EventMeta;
    eventId: string;
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedEvent: Partial<EventMeta>) => Promise<void>;
    onRemoveParticipant?: (participantId: string, participantName: string) => Promise<boolean>;
    onUpdateParticipantName?: (participantId: string, newName: string) => Promise<boolean>;
}

const EditEventModal: React.FC<EditEventModalProps> = ({
                                                           event,
                                                           eventId,
                                                           isOpen,
                                                           onClose,
                                                           onSave,
                                                           onRemoveParticipant,
                                                           onUpdateParticipantName
                                                       }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: event.name,
        description: event.description || ''
    });
    const [editingParticipant, setEditingParticipant] = useState<{id: string, name: string} | null>(null);
    const [newParticipants, setNewParticipants] = useState<string[]>([]);
    const [currentInput, setCurrentInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Share ratios being typed, by participant — only the rows that were touched,
    // so the rest keep showing whatever is saved
    const [ratioDrafts, setRatioDrafts] = useState<Record<string, string>>({});
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: event.name,
                description: event.description || ''
            });
            setNewParticipants([]);
            setCurrentInput('');
            setEditingParticipant(null);
        }
    }, [isOpen, event]);

    // Kept out of the reset above: that one also fires whenever the event changes,
    // and renaming someone inline (which saves straight away) would otherwise wipe
    // a ratio typed a moment earlier, with nothing on screen to say it was lost.
    useEffect(() => {
        if (isOpen) {
            setRatioDrafts({});
        }
    }, [isOpen]);

    const ratioValue = (participantId: string) =>
        ratioDrafts[participantId] ?? String(getShareRatio(event.shareRatios, participantId));

    // A cleared box goes back to the saved ratio, like a cleared name does
    const settleRatioDraft = (participantId: string) => {
        setRatioDrafts(prev => {
            const draft = prev[participantId];
            if (draft === undefined) return prev;
            const next = { ...prev };
            if (draft === '') {
                delete next[participantId];
            } else {
                next[participantId] = String(Math.min(Number(draft), MAX_SHARE_RATIO));
            }
            return next;
        });
    };

    const addParticipant = (name: string) => {
        const trimmedName = name.trim();
        if (trimmedName && !newParticipants.includes(trimmedName)) {
            setNewParticipants(prev => [...prev, trimmedName]);
        }
    };

    const removeNewParticipant = (index: number) => {
        setNewParticipants(newParticipants.filter((_, i) => i !== index));
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && currentInput.trim()) {
            e.preventDefault();
            addParticipant(currentInput);
            setCurrentInput('');
        } else if (e.key === 'Backspace' && !currentInput && newParticipants.length > 0) {
            setNewParticipants(prev => prev.slice(0, -1));
        }
    };

    const handleRemoveParticipant = async (participantId: string, participantName: string) => {
        if (!onRemoveParticipant) return;

        try {
            await onRemoveParticipant(participantId, participantName);
        } catch (error) {
            console.error('Error removing participant:', error);
            alert(t('event.editEvent.form.participantRemoval.error'));
        }
    };

    const handleSaveParticipantName = async (participantId: string, originalName: string) => {
        if (!onUpdateParticipantName || !editingParticipant) return;

        // If cleared, revert to original name
        const finalName = editingParticipant.name.trim() || originalName;
        
        // Only save if name actually changed
        if (finalName !== originalName) {
            try {
                await onUpdateParticipantName(participantId, finalName);
            } catch (error) {
                console.error('Error updating participant name:', error);
            }
        }
        setEditingParticipant(null);
    };

    const handleInputChange = (value: string) => {
        setCurrentInput(value);

        if (value.includes(',') || value.includes(';')) {
            const names = value.split(/[,;]/).map(n => n.trim()).filter(n => n);
            names.forEach(name => addParticipant(name));
            setCurrentInput('');
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const names = text.split(/[\n,;]/).map(n => n.trim()).filter(n => n);
            names.forEach(name => addParticipant(name));
            setCurrentInput('');
        } catch (error) {
            console.error('Failed to paste from clipboard:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert(t('event.editEvent.errors.nameRequired'));
            return;
        }

        setIsSubmitting(true);
        try {
            // Add current input if not empty
            if (currentInput.trim()) {
                addParticipant(currentInput);
            }

            const finalNewParticipants = currentInput.trim()
                ? [...newParticipants, currentInput.trim()]
                : newParticipants;

            // Only ratios off 100% are stored. The map is written whole, so one
            // left behind by someone since removed goes too — and an empty map
            // clears the node, as the Realtime Database keeps no empty objects.
            const shareRatios: ShareRatios = {};
            Object.keys(event.participants).forEach(participantId => {
                const draft = ratioDrafts[participantId];
                const ratio = draft
                    ? Math.min(Number(draft), MAX_SHARE_RATIO)
                    : getShareRatio(event.shareRatios, participantId);
                if (ratio !== DEFAULT_SHARE_RATIO) {
                    shareRatios[participantId] = ratio;
                }
            });

            // Prepare updates
            const updates: Partial<EventMeta> = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                shareRatios
            };

            if (finalNewParticipants.length > 0) {
                const newParticipantsMap: { [userId: string]: string } = {};

                await Promise.all(
                    finalNewParticipants.map(async (name) => {
                        const participantId = `~u_${generateId()}`;
                        newParticipantsMap[participantId] = name;

                        const anonymousUser: AnonymousUser = {
                            anonymousId: participantId,
                            displayName: name,
                            createdAt: Date.now(),
                            lastAccess: Date.now()
                        };

                        const eventUserRef = ref(database, `eventUsers/${eventId}/${participantId}`);
                        await set(eventUserRef, anonymousUser);
                    })
                );

                updates.participants = { ...event.participants, ...newParticipantsMap };
            }

            await onSave(updates);
            onClose();
        } catch (error) {
            console.error('Error updating event:', error);
            alert(t('event.editEvent.errors.updateFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const existingParticipantCount = Object.keys(event.participants).length;

    return (
        <div className="glass-modal-overlay">
            <div className="glass-modal max-w-lg w-full">
                {/* Header */}
                <div className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-2xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mr-3">
                                <FontAwesomeIcon icon={faEdit} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {t('event.editEvent.title')}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('event.editEvent.subtitle')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-6">
                        {/* Event Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <FontAwesomeIcon icon={faCalendarPlus} className="mr-2 text-blue-600 dark:text-blue-400" />
                                {t('event.editEvent.form.name.label')}
                                <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                                placeholder={t('event.editEvent.form.name.placeholder')}
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                <FontAwesomeIcon icon={faFileText} className="mr-2 text-blue-600 dark:text-blue-400" />
                                {t('event.editEvent.form.description.label')}
                                <span className="text-gray-400 text-xs ml-1">{t('event.editEvent.form.description.optional')}</span>
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors resize-none"
                                rows={3}
                                placeholder={t('event.editEvent.form.description.placeholder')}
                            />
                        </div>

                        {/* Current Participants */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                <FontAwesomeIcon icon={faUsers} className="mr-2 text-blue-600 dark:text-blue-400" />
                                {t('event.editEvent.form.currentParticipants.label', { count: existingParticipantCount })}
                                <HelpTip text={t('event.editEvent.form.shareRatio.help')} />
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                {t('event.editEvent.form.shareRatio.hint')}
                            </p>
                            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                                {Object.entries(event.participants).map(([participantId, participantName]) => {
                                    const ratio = ratioValue(participantId);
                                    const isCustomRatio = ratio !== String(DEFAULT_SHARE_RATIO);
                                    return (
                                        <div
                                            key={participantId}
                                            className="group flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-xl hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
                                        >
                                            <div className="flex items-center flex-1 min-w-0 mr-2">
                                                {editingParticipant?.id === participantId ? (
                                                    <input
                                                        type="text"
                                                        value={editingParticipant.name}
                                                        onChange={(e) => setEditingParticipant({ ...editingParticipant, name: e.target.value })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveParticipantName(participantId, participantName);
                                                            if (e.key === 'Escape') setEditingParticipant(null);
                                                        }}
                                                        onBlur={() => handleSaveParticipantName(participantId, participantName)}
                                                        className="w-full px-2 py-0.5 text-sm border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none"
                                                        placeholder={participantName}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <span
                                                        className={`text-gray-900 dark:text-white font-medium text-sm truncate ${
                                                            onUpdateParticipantName ? 'cursor-text hover:text-blue-600 dark:hover:text-blue-400 transition-colors' : ''
                                                        }`}
                                                        onClick={() => onUpdateParticipantName && setEditingParticipant({ id: participantId, name: participantName })}
                                                        title={onUpdateParticipantName ? t('event.editEvent.form.currentParticipants.editNameHint') : undefined}
                                                    >
                                                        {participantName}
                                                    </span>
                                                )}
                                                {participantId === event.createdBy && (
                                                    <span className="ml-2 flex-shrink-0 px-2 py-0.5 text-[10px] uppercase font-bold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
                                                        {t('event.editEvent.form.currentParticipants.ownerBadge')}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-shrink-0 items-center gap-1">
                                                {/* Reads as a quiet "100%" until someone needs it,
                                                    and stands out once it is anything else */}
                                                <label
                                                    className={`flex items-center px-1.5 py-0.5 rounded-lg border text-sm tabular-nums transition-colors focus-within:ring-2 focus-within:ring-blue-500 ${
                                                        isCustomRatio
                                                            ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-semibold'
                                                            : 'border-transparent text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                                    title={t('event.editEvent.form.shareRatio.inputLabel', { name: participantName })}
                                                >
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={ratio}
                                                        onChange={(e) => {
                                                            const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                            setRatioDrafts(prev => ({ ...prev, [participantId]: digits }));
                                                        }}
                                                        onFocus={(e) => e.target.select()}
                                                        onBlur={() => settleRatioDraft(participantId)}
                                                        aria-label={t('event.editEvent.form.shareRatio.inputLabel', { name: participantName })}
                                                        className="w-9 p-0 border-0 bg-transparent text-right focus:ring-0 focus:outline-none"
                                                    />
                                                    <span aria-hidden="true">%</span>
                                                </label>
                                                {editingParticipant?.id === participantId ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveParticipantName(participantId, participantName)}
                                                        className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                                        title={t('event.editEvent.form.currentParticipants.saveName')}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" />
                                                    </button>
                                                ) : (
                                                    participantId !== event.createdBy && onRemoveParticipant && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveParticipant(participantId, participantName)}
                                                            className="p-1.5 text-gray-300 dark:text-gray-600 group-hover:text-red-400 dark:group-hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                            title={t('event.editEvent.form.currentParticipants.removeParticipant')}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} className="w-3.5 h-3.5" />
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Add New Participants */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <FontAwesomeIcon icon={faUserPlus} className="mr-2 text-green-600 dark:text-green-400" />
                                    {t('event.editEvent.form.newParticipants.label')}
                                </label>
                                <button
                                    type="button"
                                    onClick={handlePasteFromClipboard}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
                                    title={t('event.editEvent.form.newParticipants.pasteHint')}
                                >
                                    <FontAwesomeIcon icon={faPaste} className="mr-1" />
                                    {t('event.editEvent.form.newParticipants.paste')}
                                </button>
                            </div>

                            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                                <div className="flex flex-wrap items-center gap-2">
                                    {/* New Participant Tags */}
                                    {newParticipants.map((participant, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-sm rounded-md"
                                        >
                                            {participant}
                                            <button
                                                type="button"
                                                onClick={() => removeNewParticipant(index)}
                                                className="ml-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                            </button>
                                        </span>
                                    ))}

                                    {/* Input */}
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={currentInput}
                                        onChange={(e) => handleInputChange(e.target.value)}
                                        onKeyDown={handleInputKeyDown}
                                        className="flex-1 min-w-32 px-0 py-1 border-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-0 focus:outline-none text-sm"
                                        placeholder={newParticipants.length === 0 
                                            ? t('event.editEvent.form.newParticipants.placeholder.empty') 
                                            : t('event.editEvent.form.newParticipants.placeholder.more')}
                                    />

                                    {/* Add Button */}
                                    {currentInput.trim() && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (currentInput.trim()) {
                                                    addParticipant(currentInput.trim());
                                                    setCurrentInput('');
                                                    inputRef.current?.focus();
                                                }
                                            }}
                                            className="glass-btn-primary !px-3 !py-1 text-sm"
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                            {t('event.editEvent.form.newParticipants.add')}
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                        <FontAwesomeIcon icon={faKeyboard} className="mr-1" />
                                        {t('event.editEvent.form.newParticipants.help')}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('event.editEvent.form.newParticipants.count', { count: newParticipants.length })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="glass-btn-ghost flex-1 !py-3"
                        >
                            {t('event.editEvent.buttons.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !formData.name.trim()}
                            className="glass-btn-primary flex-1 !py-3 flex items-center justify-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    {t('event.editEvent.buttons.saving')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                    {t('event.editEvent.buttons.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEventModal;
